import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { CheckMark, OnboardingHeader } from "../../components/onboarding/OnboardingBrand";
import { TwMainLayout } from "../../components/tailwind";
import { classNames } from "../../helpers";
import * as userActions from "../../redux/reducers/userReducer";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForBookings, getUrlForUsers } from "../../utils/confighelpers";
import { hasClubDetails, isSubscribedOwner, ONBOARDING_ROUTES, OWNER_BUSINESS_TYPE } from "../../utils/onboarding";
import { SELECTED_PLAN_STORAGE_KEY } from "./PlanCheckout";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

const confetti = [
  { left: "12%", color: "bg-portland-orange", shape: "h-4 w-3 rounded-sm", delay: "0s", duration: "3.6s" },
  { left: "25%", color: "bg-indigo-bz", shape: "h-3.5 w-3.5 rounded-full", delay: ".6s", duration: "4.2s" },
  { left: "39%", color: "bg-lilac", shape: "h-5 w-2.5 rounded-sm", delay: "1.2s", duration: "3.9s" },
  { left: "62%", color: "bg-portland-orange", shape: "h-3.5 w-3.5 rounded-full", delay: ".3s", duration: "4.6s" },
  { left: "75%", color: "bg-brown-coffee", shape: "h-4 w-3 rounded-sm", delay: "1.8s", duration: "3.3s" },
  { left: "88%", color: "bg-indigo-bz", shape: "h-2.5 w-2.5 rounded-full", delay: "2.2s", duration: "4.1s" },
];

const readSelectedPlan = () => {
  try {
    return JSON.parse(localStorage.getItem(SELECTED_PLAN_STORAGE_KEY) || "null");
  } catch (e) {
    return null;
  }
};

export default function Welcome({ navigation }) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const userReduxState = useSelector(userState);
  const [status, setStatus] = React.useState("activating"); // activating | active | delayed
  const [userData, setUserData] = React.useState(userReduxState?.userData || {});
  const startedAt = React.useRef(Date.now());
  const timer = React.useRef(null);

  const onBoardingData = userReduxState?.onBoardingData || {};
  const clubName = onBoardingData.clubName || userData?.myBusinessProfile?.businessName || "";
  const plan = readSelectedPlan();

  const refreshUser = async () => {
    const response = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
    if (response?.data?.statusCode === 200) {
      const data = response.data.data;
      dispatch(userActions.setUserData(data));
      setUserData(data);
      return data;
    }
    return null;
  };

  const checkActivation = async () => {
    const sessionId = searchParams.get("session_id");
    let active = false;
    if (sessionId) {
      const response = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}payments/checkStripeSubscriptionCheckoutStatus`,
        data: { session_id: sessionId, customer_id: searchParams.get("customer_id") },
      });
      const checkoutStatus = response?.data?.data?.checkoutSessionStatus;
      active = checkoutStatus === "trialing" || checkoutStatus === "active";
    }
    const latestUser = await refreshUser();
    if (latestUser && isSubscribedOwner(latestUser)) active = true;

    if (active) {
      if (latestUser && hasClubDetails(latestUser)) {
        navigation("/home/dashboard", { replace: true });
        return;
      }
      setStatus("active");
      return;
    }
    if (!sessionId && latestUser && !isSubscribedOwner(latestUser)) {
      navigation(ONBOARDING_ROUTES.plan, { replace: true });
      return;
    }
    if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
      setStatus("delayed");
      return;
    }
    timer.current = setTimeout(checkActivation, POLL_INTERVAL_MS);
  };

  React.useEffect(() => {
    if (userReduxState.businessType !== OWNER_BUSINESS_TYPE) {
      dispatch(userActions.setBusinessType(OWNER_BUSINESS_TYPE));
    }
    checkActivation();
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToDetails = () => {
    navigation(ONBOARDING_ROUTES.club, {
      state: {
        ...userData,
        clubName,
        name: userData?.fullName,
        sentFromSignupComplete: true,
        from: "welcome",
      },
    });
  };

  const trialEnd = new Date(startedAt.current + 30 * 24 * 60 * 60 * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "long" });

  const checklist = [
    { label: "Choose your plan", done: true },
    { label: "Add your club details & location", time: "2 min", action: goToDetails, primary: true },
    { label: "Connect payouts so members can pay you", time: "5 min", locked: true, lockNote: "Next, after your club details" },
    { label: "Create your first class & timetable", time: "3 min", locked: true, lockNote: "We'll show you where on your dashboard" },
    { label: "Invite your members", time: "2 min", locked: true, lockNote: "We'll show you where on your dashboard" },
  ];

  return (
    <TwMainLayout fullScreen={true}>
      <div className="relative min-h-screen overflow-hidden bg-[#F7F4F8]">
        <OnboardingHeader current={3} />

        {status === "active" && (
          <div className="pointer-events-none absolute inset-x-0 top-16 h-[440px] overflow-hidden" aria-hidden="true">
            {confetti.map((piece, i) => (
              <span
                key={i}
                className={classNames("fc-confetti absolute top-0", piece.color, piece.shape)}
                style={{ left: piece.left, animationDelay: piece.delay, animationDuration: piece.duration }}
              />
            ))}
          </div>
        )}

        {status !== "active" ? (
          <div className="flex flex-col items-center px-4 pt-24 text-center" role="status">
            {status === "activating" ? (
              <>
                <div className="h-14 w-14 animate-spin rounded-full border-[6px] border-[#EDE7F0] border-t-indigo-bz" />
                <h1 className="mt-6 text-2xl sm:text-3xl font-black text-gray-900">Activating your free trial…</h1>
                <p className="mt-2 text-gray-600">This usually takes a few seconds.</p>
              </>
            ) : (
              <>
                <h1 className="text-2xl sm:text-3xl font-black text-gray-900">This is taking longer than usual</h1>
                <p className="mt-2 max-w-lg text-gray-600">Your payment details were received. Refresh this page in a minute, or sign in again later — your trial will be there.</p>
                <button type="button" onClick={() => window.location.reload()} className="mt-6 rounded-2xl bg-brown-coffee px-6 py-3 font-extrabold text-white hover:bg-light-brown-coffee">
                  Refresh
                </button>
              </>
            )}
          </div>
        ) : (
          <main className="relative mx-auto flex max-w-6xl flex-col items-center px-4 pb-16 pt-8 sm:px-8 sm:pt-10">
            <div className="fc-pop flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-3xl bg-brown-coffee text-white shadow-[0_16px_36px_rgba(72,27,58,0.3)]">
              <CheckMark className="h-9 w-9 sm:h-11 sm:w-11" />
            </div>
            <h1 className="mt-5 text-center text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-900">
              Welcome aboard{clubName ? `, ${clubName}` : ""}!
            </h1>
            <p className="mt-3 max-w-2xl text-center text-base sm:text-lg text-gray-600">
              Your 30-day free trial is active. Add your club details next — most clubs are ready for bookings in about 15 minutes.
            </p>

            <div className="mt-8 flex w-full flex-col gap-5 lg:flex-row">
              <section className="flex-1 rounded-3xl border border-gray-200 bg-white p-5 sm:p-7 shadow-[0_20px_50px_rgba(72,27,58,0.08)]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg sm:text-xl font-black text-gray-900">Set up your club</h2>
                  <span className="text-sm font-extrabold text-indigo-bz">1 of {checklist.length} done</span>
                </div>
                <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[#EDE7F0]">
                  <div className="h-full rounded-full bg-indigo-bz" style={{ width: `${100 / checklist.length}%` }} />
                </div>
                <ul className="mt-4 divide-y divide-gray-100">
                  {checklist.map((item) => (
                    <li key={item.label} className="flex items-center gap-3 sm:gap-4 py-3.5">
                      <span
                        className={classNames(
                          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                          item.done ? "bg-bz-status-green text-white" : item.primary ? "border-2 border-indigo-bz" : "border-2 border-gray-300"
                        )}
                      >
                        {item.done && <CheckMark className="h-4 w-4" />}
                      </span>
                      <span className={classNames("flex-1 text-sm sm:text-base", item.done ? "font-bold text-gray-400 line-through" : item.locked ? "font-semibold text-gray-500" : "font-extrabold text-gray-900")}>
                        {item.label}
                        {item.locked && <span className="block text-xs font-semibold text-gray-400">{item.lockNote}</span>}
                      </span>
                      {item.time && !item.done && <span className="hidden sm:block text-xs text-gray-500">{item.time}</span>}
                      {item.action && (
                        <button type="button" onClick={item.action} className="rounded-xl bg-brown-coffee px-4 py-2 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                          Start
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </section>

              <aside className="flex w-full flex-col gap-5 lg:w-[340px]">
                <div className="rounded-3xl bg-brown-coffee p-6 text-white">
                  <p className="text-xs font-extrabold tracking-wide text-[#F7C6CC]">YOUR FREE TRIAL</p>
                  <p className="mt-2 text-lg font-extrabold">{plan?.title ? `${plan.title} plan` : "Your plan"}</p>
                  <p className="mt-1 text-sm text-[#E9DDE6]">Free until {trialEnd}. Change plan or cancel anytime from your account.</p>
                </div>
                <div className="rounded-3xl border border-gray-200 bg-white p-6">
                  <p className="text-base font-extrabold text-gray-900">Next up: your club details</p>
                  <p className="mt-1 text-sm text-gray-600">Logo, address and contact details — this is what your members will see.</p>
                  <button type="button" onClick={goToDetails} className="fc-pulse mt-4 w-full rounded-2xl bg-brown-coffee py-3 font-extrabold text-white hover:bg-light-brown-coffee">
                    Add my club details
                  </button>
                </div>
              </aside>
            </div>
          </main>
        )}
      </div>
    </TwMainLayout>
  );
}
